/**
 * A7Y-2814: the visitor-facing referral offer — a read-only layer beside
 * affiliate attribution, never part of it.
 *
 * The offer is resolved server-side: the edge worker sees `?via=` and injects
 * `<meta name="a7y-referral-offer" content='{"discountPercent":25}'>`, and the
 * prerender bootstrap copies that content into `sessionStorage` synchronously
 * at parse time so it survives the native `/` → `/scan` document navigation.
 *
 * Three invariants this module must keep:
 *
 *  1. **No pre-consent network call.** `ensureReferralOffer` fetches only when
 *     the synchronous path is empty AND `hasMarketingConsent()` is already
 *     true AND a durable affiliate token exists (A7Y-2192).
 *  2. **No durable copy of the offer.** The discount is server-owned and a
 *     superadmin can change it at any time, so a cached percentage would
 *     recreate the promise-vs-Stripe mismatch this ticket removes (D9). The
 *     session cache is per-tab and dies with it.
 *  3. **`affiliate_tracking.ts` is not touched.** Attribution capture, the
 *     visit POST, and every durable write stay exactly as they are; only
 *     `LS_TOKEN` is read from here.
 *
 * A zero or negative discount is not an offer — silence is the answer in every
 * partial case, because a discount claim must never appear before it is
 * substantiated (D5).
 */

import { apiGetAffiliateOffer } from '@a7y/api-client';
import { REFERRAL_OFFER_META, SS_REFERRAL_OFFER } from './affiliate-handoff-contract';
import { LS_TOKEN } from './affiliate_tracking';
import { hasMarketingConsent } from './consent';

export interface ReferralOffer {
  discountPercent: number;
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/** Coerce an unknown percent into an offer, or null when it is not renderable. */
function toOffer(percent: unknown): ReferralOffer | null {
  if (typeof percent !== 'number' || !Number.isFinite(percent) || percent <= 0) return null;
  return { discountPercent: percent };
}

/** Parse a `{"discountPercent":n}` payload. Malformed input yields null, never a throw. */
function parseOfferJson(raw: string | null | undefined): ReferralOffer | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return toOffer((parsed as { discountPercent?: unknown }).discountPercent);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function readMetaOffer(): ReferralOffer | null {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector(`meta[name="${REFERRAL_OFFER_META}"]`);
  return parseOfferJson(meta?.getAttribute('content'));
}

function readSessionOffer(): ReferralOffer | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    return parseOfferJson(window.sessionStorage.getItem(SS_REFERRAL_OFFER));
  } catch {
    // sessionStorage unavailable (blocked cookies, private mode quirks).
    return null;
  }
}

function writeSessionOffer(offer: ReferralOffer): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(SS_REFERRAL_OFFER, JSON.stringify(offer));
  } catch {
    // A storage failure must never suppress rendering the offer we already hold.
  }
}

/** The durable affiliate token written post-consent by `writeAffiliateState`. */
function readStoredAffiliateToken(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(LS_TOKEN);
    const token = raw?.trim();
    return token ? token : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Synchronous read: the worker's meta tag first, then the same-tab session
 * cache. SSR-safe — returns null when there is no document.
 */
export function readReferralOfferSync(): ReferralOffer | null {
  return readMetaOffer() ?? readSessionOffer();
}

/**
 * Boot-time capture: copy the worker's meta tag into the session cache so the
 * offer survives a same-tab document navigation. Idempotent, no network, no
 * durable write. The prerender bootstrap already does this synchronously on
 * prerendered marketing routes; this covers every other entry point.
 */
export function captureReferralOffer(): void {
  const offer = readMetaOffer();
  if (!offer) return;
  writeSessionOffer(offer);
}

/** In-flight fetch memo, so concurrent callers share one request. */
let inFlight: Promise<ReferralOffer | null> | null = null;

/**
 * Resolve the offer for this visitor.
 *
 * The synchronous path answers first. Only when it yields nothing, marketing
 * consent is already granted, and a durable affiliate token exists does this
 * fetch the public offer endpoint — a returning referred visitor whose meta
 * tag and session cache are both gone. Never fetches pre-consent.
 */
export async function ensureReferralOffer(): Promise<ReferralOffer | null> {
  const sync = readReferralOfferSync();
  if (sync) {
    writeSessionOffer(sync);
    return sync;
  }

  if (!hasMarketingConsent()) return null;

  const token = readStoredAffiliateToken();
  if (!token) return null;

  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await apiGetAffiliateOffer(token);
      if (!response.success) return null;
      const offer = toOffer(response.data?.discountPercent);
      if (!offer) return null;
      writeSessionOffer(offer);
      return offer;
    } catch {
      // Fail silent: an unresolved offer renders nothing, exactly like no offer.
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Drop the session cache (marketing-consent revocation). */
export function clearReferralOffer(): void {
  inFlight = null;
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(SS_REFERRAL_OFFER);
  } catch {
    // ignore — nothing durable was ever written
  }
}
